import type { ShipmentScan } from "@estelle/domain";
import { useState } from "react";

import { formatDate } from "@/components/table/cells";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { scanWords } from "@/modules/orders/tracking";

/** How many scans show before "Show all": enough to see where it is and how it got there. */
export const TIMELINE_COLLAPSED = 5;

/**
 * A shipment's scan history as a timeline, newest first (#527, Part B).
 *
 * Each scan says when (India time), what it means in plain words, and where,
 * with the courier's remark. The courier's own wording is there but secondary:
 * a smaller line under the plain one, only where it says something the plain
 * words do not. A status the table does not know is shown exactly as it was
 * received and carries a muted "Not recognised" mark — it is never reworded,
 * because a guess would be wrong exactly when it mattered.
 *
 * A history longer than five collapses to the latest five. The scans arrive
 * oldest first, as the API sends them, and are turned round here.
 */
export function ScanTimeline({ scans }: { scans: readonly ShipmentScan[] }) {
  const [showAll, setShowAll] = useState(false);

  const newestFirst = [...scans].reverse();
  const collapsible = newestFirst.length > TIMELINE_COLLAPSED;
  const visible =
    showAll || !collapsible
      ? newestFirst
      : newestFirst.slice(0, TIMELINE_COLLAPSED);

  return (
    <div className="space-y-2">
      <ol
        aria-label="Scan history, newest first"
        className="border-border relative ml-1.5 space-y-3 border-l"
      >
        {visible.map((scan, index) => {
          const words = scanWords(scan);
          const where = [scan.location, scan.remark]
            .map((part) => part?.trim() ?? "")
            .filter((part) => part !== "");
          return (
            <li key={scan.id} className="relative pl-4">
              <span
                aria-hidden
                className={cn(
                  "absolute top-1.5 -left-[5px] size-2.5 rounded-full border-2",
                  index === 0
                    ? "border-primary bg-primary"
                    : "border-muted-foreground/50 bg-background",
                )}
              />
              <p className="text-muted-foreground text-xs tabular-nums">
                {formatDate(scan.happenedAt, true)}
              </p>
              <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-medium">
                <span className="wrap-anywhere">{words.title}</span>
                {words.recognised ? null : (
                  <span className="text-muted-foreground border-border rounded-full border px-1.5 text-xs font-normal">
                    Not recognised
                  </span>
                )}
              </p>
              {where.length > 0 ? (
                <p className="text-muted-foreground text-sm wrap-anywhere">
                  {where.join(" · ")}
                </p>
              ) : null}
              {words.asReceived ? (
                <p className="text-muted-foreground/80 text-xs wrap-anywhere">
                  Courier said: {words.asReceived}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      {collapsible ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-primary-strong pointer-coarse:h-11 -ml-2"
          aria-expanded={showAll}
          onClick={() => {
            setShowAll((current) => !current);
          }}
        >
          {showAll
            ? `Show only the latest ${String(TIMELINE_COLLAPSED)}`
            : `Show all ${String(newestFirst.length)} scans`}
        </Button>
      ) : null}
    </div>
  );
}
